/**
 * Create Frame Modal
 *
 * Modal for creating a new frame (alternative expression) of an item.
 * Includes "Generate with AI" to get multiple framings and apply them
 * frame-by-frame to the original item.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  AlertCircle,
  Lightbulb,
  Shield,
  Hammer,
  Handshake,
  TrendingUp,
  Wand2,
  Sparkles,
  Loader2,
  Check,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { generateFramesFromIdea, type GeneratedFrame } from '../../../lib/llmShapingService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import type { CandidateLanguageType } from '../../../types/prioritization/lifecycle';
import { useItemCategoriesStore } from '../../../stores/prioritization/itemCategoriesStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { CATEGORY_FRAME_PICKER } from '../../../lib/itemCategoryColors';

interface CreateFrameModalProps {
  candidateId: string;
  candidateTitle: string;
  candidateDescription?: string;
  currentType?: CandidateLanguageType;
  /** Pre-select a frame type when the modal opens (e.g. when launched from a category tile click). */
  initialType?: CandidateLanguageType;
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (frameId: string, frameType: CandidateLanguageType, isCanonical: boolean) => void;
}

const FRAME_TYPES: Array<{
  type: CandidateLanguageType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  example: string;
  reframingHint: string;
}> = [
  {
    type: 'problem',
    label: 'Problem',
    icon: AlertCircle,
    color: CATEGORY_FRAME_PICKER.problem,
    example: 'Customer churn rate is 8%',
    reframingHint: 'What is wrong that needs fixing?',
  },
  {
    type: 'opportunity',
    label: 'Opportunity',
    icon: Lightbulb,
    color: CATEGORY_FRAME_PICKER.opportunity,
    example: 'Increase retention to 96%',
    reframingHint: 'What could we gain by addressing this?',
  },
  {
    type: 'risk',
    label: 'Risk',
    icon: Shield,
    color: CATEGORY_FRAME_PICKER.risk,
    example: 'Competitor may launch first',
    reframingHint: 'What might go wrong if we don\'t act?',
  },
  {
    type: 'solution',
    label: 'Solution',
    icon: Hammer,
    color: CATEGORY_FRAME_PICKER.solution,
    example: 'Build a loyalty program',
    reframingHint: 'What specific approach could we take?',
  },
  {
    type: 'commitment',
    label: 'Commitment',
    icon: Handshake,
    color: CATEGORY_FRAME_PICKER.commitment,
    example: 'Deliver feature X by Q2',
    reframingHint: 'What promise have we made externally?',
  },
  {
    type: 'investment',
    label: 'Investment',
    icon: TrendingUp,
    color: CATEGORY_FRAME_PICKER.investment,
    example: 'Fund R&D for new market',
    reframingHint: 'What strategic bet are we making?',
  },
];

const VALID_CANDIDATE_TYPES: CandidateLanguageType[] = FRAME_TYPES.map(c => c.type);

function getCurrentFrameLabel(currentType: CandidateLanguageType | string | undefined): string | null {
  if (!currentType || !VALID_CANDIDATE_TYPES.includes(currentType as CandidateLanguageType)) return null;
  return FRAME_TYPES.find(c => c.type === currentType)?.label ?? null;
}

export function CreateFrameModal({
  candidateId,
  candidateTitle,
  candidateDescription,
  currentType,
  initialType,
  isOpen,
  onClose,
  onCreated,
}: CreateFrameModalProps) {
  const shapingEnabled = useAiCapability('shaping');
  const [selectedType, setSelectedType] = useState<CandidateLanguageType | null>(initialType ?? null);

  // Sync pre-selected type and reset canonical toggle whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedType(initialType ?? null);
      setMakeCanonical(true);
    }
  }, [isOpen, initialType]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Default to true: when adding a new alternate frame, make it active so altitude questions update immediately
  const [makeCanonical, setMakeCanonical] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generatedResult, setGeneratedResult] = useState<{
    frames: GeneratedFrame[];
    originalType: CandidateLanguageType;
  } | null>(null);
  const [addedGeneratedIndices, setAddedGeneratedIndices] = useState<Set<number>>(new Set());

  const { addFrame, getFramesForItem } = useItemsStore();
  const userId = useUserStore(state => state.userId);

  const wsCategories = useItemCategoriesStore((s) => s.categories);
  const fetchWsCategories = useItemCategoriesStore((s) => s.fetchCategories);
  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId) fetchWsCategories(workspaceId);
  }, [fetchWsCategories]);
  const activeCategorySlugs = wsCategories.length > 0
    ? new Set(wsCategories.filter((c) => c.isActive).map((c) => c.slug))
    : null;
  const visibleFrameTypes = activeCategorySlugs
    ? FRAME_TYPES.filter((ft) => activeCategorySlugs.has(ft.type))
    : FRAME_TYPES;

  if (!isOpen) return null;

  const existingFrameTypes = (getFramesForItem(candidateId) || []).map(f => f.frameType);

  const selectedConfig = selectedType 
    ? FRAME_TYPES.find(t => t.type === selectedType)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !title.trim() || !userId) return;

    setIsSubmitting(true);

    const frameId = addFrame(candidateId, {
      frameType: selectedType,
      title: title.trim(),
      description: description.trim(),
      createdBy: userId,
      createdFor: undefined,
      isCanonical: makeCanonical,
    });

    setIsSubmitting(false);
    onCreated?.(frameId, selectedType, makeCanonical);
    handleClose();
  };

  const handleClose = () => {
    setSelectedType(null);
    setTitle('');
    setDescription('');
    setMakeCanonical(true);
    setGeneratedResult(null);
    setGenerateError(null);
    setAddedGeneratedIndices(new Set());
    onClose();
  };

  const handleGenerateWithAI = async () => {
    setGenerateError(null);
    setGeneratedResult(null);
    setAddedGeneratedIndices(new Set());
    setGenerating(true);
    try {
      const res = await generateFramesFromIdea({
        title: candidateTitle,
        description: candidateDescription ?? undefined,
      });
      setGeneratedResult(res);
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddGeneratedFrame = (idx: number) => {
    if (!generatedResult || !userId) return;
    const frame = generatedResult.frames[idx];
    const isOriginal = frame.frameType === generatedResult.originalType;
    const frameId = addFrame(candidateId, {
      frameType: frame.frameType,
      title: frame.title,
      description: frame.description,
      createdBy: userId,
      createdFor: 'llm-generated',
      isCanonical: makeCanonical,
      isOriginal,
    });
    if (frameId) {
      onCreated?.(frameId, frame.frameType, makeCanonical);
      // Keep the modal open so the user can add more frames
      setAddedGeneratedIndices(prev => new Set(prev).add(idx));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-[var(--bg-raised)]">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">
              Add an Additional Category
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Original item context */}
          <div className="bg-[var(--surface)] p-4">
            <p className="text-sm text-[var(--text-ter)] mb-1">Adding a category for:</p>
            <p className="font-medium text-[var(--text-pri)]">{candidateTitle}</p>
            {getCurrentFrameLabel(currentType) ? (
              <p className="text-sm text-[var(--text-ter)] mt-1">
                Item Category: <span className="font-medium">{getCurrentFrameLabel(currentType)}</span>
              </p>
            ) : (
              <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] mt-1">
                Item Category not set. Set it above before adding more categories.
              </p>
            )}
          </div>

          {/* Active frame toggle — applies to both AI adds and manual creation */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={makeCanonical}
              onChange={(e) => setMakeCanonical(e.target.checked)}
              className="w-4 h-4 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500"
            />
            <span className="text-sm text-[var(--text-sec)]">
              Set as the active category for this item
            </span>
          </label>

          {/* Generate with AI – apply frame by frame */}
          <div className="border border-[var(--slate)] dark:border-[var(--slate)] overflow-hidden">
            <div className="px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-b border-[var(--slate)] dark:border-[var(--slate)] flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                <span className="font-medium text-[var(--text-pri)]">Generate with AI</span>
              </div>
              {shapingEnabled && !generatedResult ? (
                <button
                  type="button"
                  onClick={handleGenerateWithAI}
                  disabled={generating}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {generating ? 'Generating…' : 'Generate categories'}
                </button>
              ) : shapingEnabled ? (
                <button
                  type="button"
                  onClick={handleGenerateWithAI}
                  disabled={generating}
                  className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                >
                  Regenerate
                </button>
              ) : null}
            </div>
            {generateError && (
              <div className="px-4 py-2 bg-[var(--coral-dim)] text-[var(--coral)] text-sm border-b border-[var(--coral)]">
                {generateError}
              </div>
            )}
            {generatedResult && generatedResult.frames.length > 0 && (
              <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
                <p className="text-xs text-[var(--text-ter)] mb-2">
                  Add any category to the item. Apply one at a time.
                </p>
                {generatedResult.frames.map((frame, idx) => {
                  const config = FRAME_TYPES.find(c => c.type === frame.frameType);
                  const Icon = config?.icon ?? RefreshCw;
                  const alreadyAdded = addedGeneratedIndices.has(idx);
                  const alreadyHasThisType = existingFrameTypes.includes(frame.frameType);
                  const canAdd = !alreadyAdded && !alreadyHasThisType;
                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 p-3  border ${
                        alreadyAdded
                          ? 'bg-[var(--green-dim)] border-[var(--green)] dark:bg-[var(--green)]/20 dark:border-[var(--green)]'
                          : 'bg-[var(--bg-raised)] border-[var(--rule)]'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon className="h-4 w-4 text-[var(--text-ter)]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-[var(--text-pri)]">
                            {config?.label ?? frame.frameType}
                          </span>
                          {frame.frameType === generatedResult.originalType && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)]">
                              Original
                            </span>
                          )}
                          {alreadyAdded && (
                            <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
                              <Check className="h-3 w-3" /> Added
                            </span>
                          )}
                          {alreadyHasThisType && !alreadyAdded && (
                            <span className="text-xs text-[var(--text-ter)]">Already have this type</span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--text-sec)] truncate mt-0.5" title={frame.title}>
                          {frame.title}
                        </p>
                        {frame.description && (
                          <p className="text-xs text-[var(--text-ter)] line-clamp-2 mt-0.5">
                            {frame.description}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {canAdd && (
                          <button
                            type="button"
                            onClick={() => handleAddGeneratedFrame(idx)}
                            className="px-2 py-1 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] border border-[var(--slate)] dark:border-[var(--slate)] rounded hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20"
                          >
                            Add this category
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider: or create manually */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--rule)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[var(--bg-raised)] px-2 text-xs text-[var(--text-ter)]">Or create one manually</span>
            </div>
          </div>

          {/* Frame type selection */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-3">
              Select category <span className="text-[var(--coral)]">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {visibleFrameTypes.map(config => {
                const Icon = config.icon;
                const isSelected = selectedType === config.type;
                const isCurrent = Boolean(currentType && VALID_CANDIDATE_TYPES.includes(currentType) && currentType === config.type);
                const alreadyExists = existingFrameTypes.includes(config.type);
                const isDisabled = isCurrent || alreadyExists;

                return (
                  <button
                    key={config.type}
                    type="button"
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedType(config.type);
                      // If the AI has already generated a frame for this type,
                      // pre-fill the manual form so the user can review / edit it.
                      if (generatedResult) {
                        const aiFrame = generatedResult.frames.find(
                          (f) => f.frameType === config.type,
                        );
                        if (aiFrame) {
                          setTitle(aiFrame.title);
                          setDescription(aiFrame.description ?? '');
                        } else {
                          setTitle('');
                          setDescription('');
                        }
                      }
                    }}
                    disabled={isDisabled}
                    data-selected={isSelected}
                    className={`flex flex-col items-center gap-2 p-4  border-2 transition-all text-[var(--text-sec)] ${config.color} ${
                      isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="font-medium text-sm">{config.label}</span>
                    {isCurrent && (
                      <span className="text-xs text-[var(--text-ter)]">(current)</span>
                    )}
                    {alreadyExists && !isCurrent && (
                      <span className="text-xs text-[var(--text-ter)]">(exists)</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reframing hint */}
          {selectedConfig && (
            <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] p-3">
              <div className="flex items-start gap-2">
                <Wand2 className="w-4 h-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-sec)]">
                    Hint:
                  </p>
                  <p className="text-sm text-[var(--text-sec)] mt-0.5">
                    {selectedConfig.reframingHint}
                  </p>
                  <p className="text-xs text-[var(--text-sec)] mt-2">
                    Example: "{selectedConfig.example}"
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="frame-title" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Title for this category <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              id="frame-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={selectedConfig 
                ? `Express as a ${selectedConfig.label.toLowerCase()}...`
                : 'Select a category first'
              }
              disabled={!selectedType}
              className="w-full px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-[var(--hover-tint)] dark:disabled:bg-[var(--bg-raised)]"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="frame-description" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Description for this category
            </label>
            <textarea
              id="frame-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more context for this category..."
              rows={3}
              disabled={!selectedType}
              className="w-full px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-[var(--hover-tint)] dark:disabled:bg-[var(--bg-raised)]"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              {addedGeneratedIndices.size > 0 ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={!selectedType || !title.trim() || isSubmitting}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] dark:disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Add Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateFrameModal;
